import type { GetPositionsRow } from '@morfi/backend-sdk-v2/client';

import { isClosedDustResidual } from '@/lib/market/position-visibility';
import { isDustD6 } from '@/lib/validation/dust';

const SIGNED_INTEGER = /^-?\d+$/;
const UNSIGNED_INTEGER = /^\d+$/;

export type InvalidLifecycleAmountField =
  | 'balance'
  | 'costBasisD6'
  | 'realizedCostBasisGrossD6'
  | 'realizedPnlD6';

export interface PortfolioPositionVisibility {
  isVisible: boolean;
  /** Non-empty only when malformed amounts prevent a safe ghost decision. */
  invalidFields: readonly InvalidLifecycleAmountField[];
}

/**
 * Applies the canonical closed-ghost contract without letting malformed wire
 * amounts erase portfolio history. Invalid amounts warn only when every valid
 * gate still points at a ghost; a meaningful valid amount already proves the
 * row belongs in the portfolio and needs no warning.
 */
export function evaluatePortfolioPositionVisibility(
  row: GetPositionsRow,
): PortfolioPositionVisibility {
  if (row.resultState !== 'sold_out' || row.prizeState !== 'none') {
    return { isVisible: true, invalidFields: [] };
  }

  const balanceIsValid = UNSIGNED_INTEGER.test(row.balance);
  const realizedPnlIsValid = SIGNED_INTEGER.test(row.realizedPnlD6);
  const costBasisIsValid = SIGNED_INTEGER.test(row.costBasisD6);
  const releasedBasisIsValid = UNSIGNED_INTEGER.test(row.realizedCostBasisGrossD6);
  const invalidFields = [
    balanceIsValid ? null : ('balance' as const),
    costBasisIsValid ? null : ('costBasisD6' as const),
    releasedBasisIsValid ? null : ('realizedCostBasisGrossD6' as const),
    realizedPnlIsValid ? null : ('realizedPnlD6' as const),
  ].filter((field): field is InvalidLifecycleAmountField => field !== null);

  if (invalidFields.length === 0) {
    return { isVisible: !isClosedDustResidual(row), invalidFields };
  }

  const validAmountRequiresVisibility =
    (balanceIsValid && BigInt(row.balance) > BigInt(0)) ||
    (costBasisIsValid && !isDustD6(row.costBasisD6)) ||
    (releasedBasisIsValid && !isDustD6(row.realizedCostBasisGrossD6)) ||
    (realizedPnlIsValid && !isDustD6(row.realizedPnlD6));

  return {
    isVisible: true,
    invalidFields: validAmountRequiresVisibility ? [] : invalidFields,
  };
}
